import { Result } from './Result';

/**
 * Represents a resource that can be asynchronously loaded. Generic over the resource (`T`)
 * and an error type (`E`).
 *
 * Usage:
 * ```
 * const myResource: LoadableResource<number, string>
 *
 * if (myResource.isLoaded) {
 *   myResource.value // number
 * } else if (myResource.isError) {
 *   myResource.error // string
 * } else if (myResource.isLoading) {
 *   myResource.value // number | undefined
 *   myResource.error // string | undefined
 * } else {
 *   ...
 * }
 *
 * ```
 */
export type LoadableResource<T, E> =
  | {
      state: 'not-loaded';
      isNotLoaded: true;
      isError: false;
      isLoaded: false;
      isLoading: false;
      value?: undefined;
      error?: undefined;
    }
  | {
      state: 'loading';
      isNotLoaded: false;
      isError: false;
      isLoaded: false;
      isLoading: true;
      value?: T;
      error?: E;
    }
  | {
      state: 'loaded';
      isNotLoaded: false;
      isError: false;
      isLoaded: true;
      isLoading: false;
      value: T;
      error?: undefined;
    }
  | {
      state: 'error';
      isNotLoaded: false;
      isError: true;
      isLoaded: false;
      isLoading: false;
      value?: undefined;
      error: E;
    };

export const LoadableResource = {
  /**
   * Creates a new loadable resource in the `not-loaded` state.
   */
  notLoaded: <T, E>(): LoadableResource<T, E> => {
    return {
      state: 'not-loaded',

      isNotLoaded: true,
      isError: false,
      isLoaded: false,
      isLoading: false,
    };
  },
  /**
   * Creates a new loadable resource in the `loading` state, with an optional `value` or `error`.
   */
  loading: <T, E>(options?: { value?: T } | { error?: E }): LoadableResource<T, E> => {
    return {
      state: 'loading',
      isNotLoaded: false,
      isError: false,
      isLoaded: false,
      isLoading: true,
      value: undefined,
      error: undefined,
      ...options,
    };
  },
  /**
   * Creates a new loadable resource in the `loaded` state, having the provided `value`.
   */
  loaded: <T, E>(value: T): LoadableResource<T, E> => {
    return {
      state: 'loaded',
      isNotLoaded: false,
      isError: false,
      isLoaded: true,
      isLoading: false,
      value,
    };
  },
  /** Creates a new loadable resource in the `error` state, having the provided `error`. */
  error: <T, E>(error: E): LoadableResource<T, E> => {
    return {
      state: 'error',
      isNotLoaded: false,
      isError: true,
      isLoaded: false,
      isLoading: false,
      error,
    };
  },
  /**
   * Creates a new `LoadableResource` from the provided `Result`.
   *
   * The resource will be `loaded` in the case of a `success` result, and `error` in the
   * case of a `failure` result.
   */
  fromResult: <T, E>(result: Result<T, E>): LoadableResource<T, E> => {
    if (result.success) {
      return LoadableResource.loaded(result.value);
    } else {
      return LoadableResource.error(result.error);
    }
  },
};
